/**
 * Admissions CRM — Inquiries API contract (plan #001, U5).
 *
 * GET /v1/enrollment/inquiries
 *
 * Admin-only, school-scoped read of mirrored Blackbaud inquiries: a
 * new-this-week count (inquiries with `dateSubmitted` in the last 7 days) plus
 * a list (R12). A CONVERTED inquiry (`userId` non-null) carries a reference to
 * its candidate so the web can deep-link the converted row to the candidate
 * drawer. School is the active-school context; cross-school is empty by
 * construction (R14).
 */

import { z } from 'zod'

/** One inquiry row. */
export const EnrollmentInquiryRowSchema = z.object({
  /** Inquiry row publicId. */
  publicId: z.string(),
  /** Prospect first name, or null. */
  firstName: z.string().nullable(),
  /** Prospect last name, or null. */
  lastName: z.string().nullable(),
  /** Prospect email, or null. */
  email: z.string().nullable(),
  /** Entering year label, or null. */
  enteringYear: z.string().nullable(),
  /** Submission timestamp (ISO 8601), or null. */
  dateSubmitted: z.string().nullable(),
  /** Processed timestamp (ISO 8601), or null. */
  dateProcessed: z.string().nullable(),
  /** True when this inquiry converted to a candidate (`userId` non-null). */
  isConverted: z.boolean(),
  /**
   * The converted candidate's BB id (the inquiry's `userId`), or null. Joins to
   * a candidate row when one is mirrored — `candidatePublicId` carries the link.
   */
  candidateBbId: z.number().int().nullable(),
  /** Candidate row publicId when the converted candidate is mirrored, else null. */
  candidatePublicId: z.string().nullable()
})
export type EnrollmentInquiryRow = z.infer<typeof EnrollmentInquiryRowSchema>

export const GetEnrollmentInquiriesResponseSchema = z.object({
  /** Total mirrored inquiries at the school (within scope). */
  total: z.number().int().nonnegative(),
  /** Inquiries with `dateSubmitted` in the last 7 days (R12). */
  newThisWeek: z.number().int().nonnegative(),
  /** True when the row list was capped server-side (bounded-work pattern). */
  isTruncated: z.boolean(),
  /** The inquiry rows (capped server-side; the counts above are exact). */
  rows: z.array(EnrollmentInquiryRowSchema)
})
export type GetEnrollmentInquiriesResponse = z.infer<typeof GetEnrollmentInquiriesResponseSchema>
